import { type FC, memo, useMemo } from 'react';
import { Platform, View } from 'react-native';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';

import { androidScaleSize } from '../../../../shared/theme/androidUiParity';
import { layoutLtrStyle } from '../../../../shared/i18n/rtl';
import { AnimatedDigit } from './AnimatedDigit';
import type { AnimatedCountProps } from './types';
import type { SharedValue } from 'react-native-reanimated';

/** Pull digit columns slightly together so multi-digit values read as one number. */
const DIGIT_COLUMN_OVERLAP = Platform.OS === 'android' ? androidScaleSize(3) : 2;

function visibleDigitCount(count: number, max_digits: number): number {
  'worklet';
  const n = Math.max(0, Math.floor(Math.abs(count)));
  const len = n === 0 ? 1 : Math.floor(Math.log10(n)) + 1;
  return Math.max(1, Math.min(max_digits, len));
}

/**
 * Multi-digit readout for wheel pickers.
 * Always LTR + MSD→LSD row order so RTL app locale cannot reverse digits into values like `001` for `100`.
 */
export const AnimatedCount: FC<AnimatedCountProps> = memo(
  ({
    count,
    maxDigits,
    textDigitHeight = 60,
    textDigitWidth = 35,
    fontSize = 50,
    color = 'black',
    style,
  }) => {
    const effective_digit_width = textDigitWidth - DIGIT_COLUMN_OVERLAP;

    const digit_text_style = useMemo(
      () => ({
        color,
        fontSize,
        fontWeight: 'bold' as const,
        writingDirection: 'ltr' as const,
      }),
      [color, fontSize],
    );

    const viewport_style = useAnimatedStyle(() => {
      const visible_digits = visibleDigitCount(count.value, maxDigits);
      const w = effective_digit_width * visible_digits + DIGIT_COLUMN_OVERLAP;
      return {
        width: w,
        minWidth: w,
      };
    }, [effective_digit_width, maxDigits]);

    return (
      <Animated.View
        style={[
          {
            overflow: 'hidden',
            justifyContent: 'center',
            height: textDigitHeight,
          },
          viewport_style,
          style,
        ]}>
        <View
          style={[
            layoutLtrStyle,
            {
              flexDirection: 'row',
              alignItems: 'flex-end',
              height: textDigitHeight,
            },
          ]}>
          {new Array(maxDigits).fill(0).map((_, index) => (
            <AnimatedDigit
              key={index}
              index={index}
              count={count as SharedValue<number>}
              maxDigits={maxDigits}
              height={textDigitHeight}
              width={effective_digit_width}
              textStyle={digit_text_style}
            />
          ))}
        </View>
      </Animated.View>
    );
  },
);
